<script>
/**
 * The MatchTeamGameRegistration view works for the registration work for
 * both teams of the game. 
 */

//--------------------------------------------------
// imported classes
//--------------------------------------------------
import { Tabs, Tab, Dialog } from 'vant'
import { DateUtil } from '@/utils'
import ObjectFunc from '@/utils/ObjectFunc'
import { getPassport } from '@/libs/passport'
import GameSummary from './game-summary.vue'
import TeamPlayerEvents from './team-player-events.vue'
import { getGameDetail, getGameEventsByPlayer, getMatchGameTeamStartingList, getGameTeamConfirmed, updateGameTeamConfirmed, updateGameRefereeConfirmed } from '@/api/match-team-game'

//--------------------------------------------------
// main script
//--------------------------------------------------
export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'match-team-game-confirmation',

    //--------------------------------------------------
    // sub-components
    //--------------------------------------------------
	components: {
        /* The game summary */
        'game-summary': GameSummary,
        'team-player-events': TeamPlayerEvents,
        'van-tabs': Tabs,
        'van-tab': Tab
	},

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            /* The game info */
            gameInfo: {
                /* The title string */
                title: null,
                /* The time string */
                time: null,
                /* The home team id */
                homeTeamId: null,
                /* The home team name */
                homeTeamName: null,
                /* The home team badge */
                homeTeamBadge: null,
                /* The home team score */
                homeTeamScore: null,
                /* The away team id */
                awayTeamId: null,
                /* The away team name */
                awayTeamName: null,
                /* The away team badge */
                awayTeamBadge: null,
                /* The away team score */
                awayTeamScore: null,
            },
            /* The selected team tab which includes home(0) and away(1) */
            selectedTeamTab: 0,
            /**
             * The player event list for the home team.
             * The list contains the item with {id, number, name, isStarter, events}.
             * The events contains the item with {id, time, type}.
             * The type enums goal, ownGoal, penalty, assist, red, yellow, changes, 
             * changes_down.
             */
            homePlayerEventList: null,
            /**
             * The starter/bench player list for the home team.
             * The list contains the item with {id, name, number, position}
             */
            homePlayerList: null,
            /**
             * The player event list for the away team.
             * The list contains the item with {id, number, name, isStarter, events}.
             * The events contains the item with {id, time, type}.
             * The type enums goal, ownGoal, penalty, assist, red, yellow, changes, 
             * changes_down.
             */
            awayPlayerEventList: null,
            /**
             * The starter/bench player list for the away team.
             * The list contains the item with {id, name, number, position}
             */
            awayPlayerList: null,
            /**
             * The game confirmed status.
             */
            gameConfirmed: false,
            /**
             * The home team confirmed data includes the user and time.
             */
            homeConfirmed: {
                user: null,
                time: null
            },
            /**
             * The away team confirmed data includes the user and time.
             */
            awayConfirmed: {
                user: null,
                time: null
            },
            /**
             * The event mode flag
             */
            isEvent: true,
            /**
            * The has away team visited flag
            */
            hasAwayTeamVisited: false
        }
    },

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        /**
         * @getter
         * Get the home team label.
         */
        homeTeamLabel() {
            return '主队：' + this.gameInfo.homeTeamName
        },
        /**
         * @getter
         * Get the away team label.
         */
        awayTeamLabel() {
            return '客队：' + this.gameInfo.awayTeamName
        },
        /**
         * @getter
         * Get the confirmed message label.
         */
        confirmedMessage() {
            let message = ''
            if (this.selectedTeamTab == 0 && this.homeConfirmed.user) {
                const date = new Date(this.homeConfirmed.time)
                message += this.homeConfirmed.user + '于' 
                    + ObjectFunc.pad(date.getMonth() + 1, 2) + '/' + ObjectFunc.pad(date.getDate(), 2) + ' ' 
                    + ObjectFunc.pad(date.getHours(), 2) + ':' + ObjectFunc.pad(date.getMinutes(), 2)
                    + '完成赛后确认'
            }
            else if (this.selectedTeamTab == 1 && this.awayConfirmed.user) {
                const date = new Date(this.awayConfirmed.time)
                message += this.awayConfirmed.user + '于' 
                    + ObjectFunc.pad(date.getMonth() + 1, 2) + '/' + ObjectFunc.pad(date.getDate(), 2) + ' ' 
                    + ObjectFunc.pad(date.getHours(), 2) + ':' + ObjectFunc.pad(date.getMinutes(), 2)
                    + '完成赛后确认'
            }
            
            return message
        }
    },

    methods: {
        //--------------------------------------------------
        // request method
        //--------------------------------------------------
        /**
         * @request
         * Query the game info and set the bindable data model.
         * @return The reponse object.
         */
        queryGameInfo() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId,
                eventMode: 0,
                simple: 0
            }
            getGameDetail(params).then(response => {
                console.log("get game detail response", response)
                const data = response.data
                // set the game info data
                let title = data.game.matchName
                if (data.game.gameType) {
                    title += ' | ' + data.game.gameType
                }
                if (data.game.roundDescription) {
                    title += ' | ' + data.game.roundDescription
                }
                this.gameInfo.title = title
                const weekDay = DateUtil.parseDateTime(data.game.beginDate, '{a}')
                console.log('weekDay', weekDay)
                const startTime = DateUtil.parseDateTime(data.game.beginDate, '{y}-{m}-{d} {h}:{i}')
                const endTime = DateUtil.parseDateTime(data.game.endDate, '{h}:{i}')
                this.gameInfo.time = '周' + weekDay + ' ' + startTime + '~' + endTime
                this.gameInfo.homeTeamId = data.game.homeTeamId
                this.gameInfo.homeTeamName = data.game.homeTeamName
                this.gameInfo.homeTeamBadge = data.game.homeTeamBadge
                if (data.game.homeScore > -1) {
                    this.gameInfo.homeTeamScore = data.game.homeScore
                }    
                this.gameInfo.awayTeamId = data.game.awayTeamId
                this.gameInfo.awayTeamName = data.game.awayTeamName
                this.gameInfo.awayTeamBadge = data.game.awayTeamBadge
                if (data.game.awayScore > -1) {
                    this.gameInfo.awayTeamScore = data.game.awayScore
                } 
                // query the game event by sequence
                this.queryGameEvent()
            })
        },
        queryGameEvent() {
            const routerParams = this.$route.params
            const params = {
                gameId: routerParams.gameId
            }
            getGameEventsByPlayer(params).then(response => {
                console.log("get game events response", response.data)
                const data = response.data
                this.homePlayerEventList = this.toTeamPlayerEventList(data.homePlayers)
                this.sortPlayerList(this.homePlayerEventList)
                this.awayPlayerEventList = this.toTeamPlayerEventList(data.awayPlayers)
                this.sortPlayerList(this.awayPlayerEventList)
                //
                this.queryGameConfirmation()
            })
        },
        queryGameConfirmation() {
            const routerParams = this.$route.params
            const role = this.$route.query.role
            const params = {
                gameId: routerParams.gameId
            }
            getGameTeamConfirmed(params).then(response => {
                console.log('get game team confirmed response', response.data)
                const data = response.data
                if (role == 'referee' && data.refereeConfirmed == 1) {
                    this.gameConfirmed = true
                    if (data.homeConfirmed == 1) {
                        this.homeConfirmed.user = data.homeConfirmedUserName
                        this.homeConfirmed.time = data.homeConfirmedTime
                    }
                    if (data.awayConfirmed == 1) {
                        this.awayConfirmed.user = data.awayConfirmedUserName
                        this.awayConfirmed.time = data.awayConfirmedTime
                    }
                }
                else if (role == 'home' && data.homeConfirmed == 1) {
                    this.gameConfirmed = true
                    this.homeConfirmed.user = data.homeConfirmedUserName
                    this.homeConfirmed.time = data.homeConfirmedTime
                }
                else if (role == 'away' && data.awayConfirmed == 1) {
                    this.gameConfirmed = true
                    this.awayConfirmed.user = data.awayConfirmedUserName
                    this.awayConfirmed.time = data.awayConfirmedTime
                }
            })
        },
        queryGameTeamPlayerList(isHome) {
            const routerParams = this.$route.params
            const params = {
                matchId: routerParams.matchId,
                gameId: routerParams.gameId,
                teamId: isHome ? this.gameInfo.homeTeamId : this.gameInfo.awayTeamId
            }
            getMatchGameTeamStartingList(params).then(response => {
                console.log('get match game team starting response', response.data)
                const data = response.data
                let playerList = []
                for (let i = 0; i < data.list.length; i++) {
                    let player = data.list[i]
                    if (player.isStarting > -1) {
                        playerList.push({
                            id: player.id,
                            number: player.number,
                            name: player.trueName,
                            status: player.isStarting == 1 ? 1 : 2,
                            isStarter: player.isStarting
                        })
                    }
                }
                this.sortPlayerList(playerList)
                if (isHome) {
                    this.homePlayerList = playerList
                }
                else {
                    this.awayPlayerList = playerList
                }
            })
        },

        //--------------------------------------------------
        // handler method
        //--------------------------------------------------
        /**
         * @handler
         * Handle the switch home tab click event.
         */
        onHomeSwitchTab() {
            console.log('onHomeSwitchTab')
            this.selectedTeamTab = 1
            this.hasAwayTeamVisited = true
            if (this.isEvent === false && this.awayPlayerList == null) {
                this.queryGameTeamPlayerList()
            }
        },
        /**
         * @handler
         * Handle the switch away tab click event.
         */
        onAwaySwitchTab() {
            console.log('onAwaySwitchTab')
            this.selectedTeamTab = 0
            if (this.isEvent === false && this.homePlayerList == null) {
                this.queryGameTeamPlayerList(true)
            }
        },
        /**
         * @handler
         * Handle the switch button click event.
         */
        onSwitchBtnClick() {
            console.log('onSwitchBtnClick')
            this.isEvent = !this.isEvent
            if (this.isEvent === false) {
                if (this.selectedTeamTab === 0 && this.homePlayerList == null) {
                    this.queryGameTeamPlayerList(true)
                }
                else if (this.selectedTeamTab === 1 && this.awayPlayerList == null) {
                    this.queryGameTeamPlayerList()
                }
            }
        },
        /**
         * @handler
         * Handle the switch button click event.
         */
        onBackBtnClick() {
            console.log('onBackBtnClick')
            // back to the landing page
            this.$router.go(-1)
        },
        /**
         * @handler
         * Handle the switch button click event.
         */
        onComfirmBtnClick() {
            console.log('onComfirmBtnClick')
            // validate the hasAwayTeamVisited or not
            if (!this.hasAwayTeamVisited) {
                Dialog.confirm({
                    title: '提醒',
                    message: '请先查看客队比赛事件后，再确认赛后信息',
                    className: 'game-confirm-dialog',
                    showCancelButton: false,
                    confirmButtonText: '前往查看'
                }).then(() => {
                    // on confirm
                    this.onHomeSwitchTab()
                }).catch(() => {
                    // on cancel
                });
            }
            else {
                this.updateConfirmed()
            }
        },

        //--------------------------------------------------
        // private method
        //--------------------------------------------------
        /**
         * @private
         * Update the confirmed status according to the user role.
         */
        updateConfirmed() {
            const role = this.$route.query.role
            const routerParams = this.$route.params
            let params = {
                gameId: routerParams.gameId
            }
            const data = {
                update: 1
            }
            if (role == 'referee') {
                updateGameRefereeConfirmed(params, data).then(response => {
                    console.log('update game referee confirm response', response.data)
                    // back to the landing page
                    this.$router.go(-1)
                })
            }
            else if (role == 'home') {
                params.teamId = this.gameInfo.homeTeamId
                updateGameTeamConfirmed(params, data).then(response => {
                    console.log('update game home confirm response', response.data)
                    // back to the landing page
                    this.$router.go(-1)
                })
            }
            else if (role == 'away') {
                params.teamId = this.gameInfo.awayTeamId
                updateGameTeamConfirmed(params, data).then(response => {
                    console.log('update game away confirm response', response.data)
                    // back to the landing page
                    this.$router.go(-1)
                })
            }
        },
        /**
         * @private
         * Sort the player list with default(0) or name(1) mode via selected 
         * sort type.
         */
        sortPlayerList(playerList) {
            playerList.sort(function(a, b) {
                if (a.number && b.number) {
                    return a.number - b.number
                }
                else if (a.number == null && b.number) {
                    return 1
                }
                else if (a.number && b.number == null) {
                    return -1
                }
                else {
                    return a.id - b.id
                }
            })
        },
        toTeamPlayerEventList(players) {
            const supportedEventType = ['goal', 'penalty', 'ownGoal', 'assist', 'yellow', 'red', 'changes', 'changes_down']
            let teamPlayerEventList = []
            for (let i = 0; i < players.length; i++) {
                let player = players[i]
                let playerEvents = []
                let hasYellowCard
                let hasRedCard
                for (let j = 0; j < player.events.length; j++) {
                    let playerEvent = player.events[j]
                    let type
                    // @avoid issue 
                    // There exists the unsupported event type via API service.
                    // validate the event type from the supported one
                    // it seems the API service has fixed the issue
                    if (supportedEventType.indexOf(playerEvent.type) == -1) {
                        // console.log('missing type', playerEvent.type)
                        continue;
                    }
                    if (playerEvent.type == 'ownGoal') {
                        type = 'own-goal'
                    }
                    else if (playerEvent.type == 'changes') {
                        type = 'substitution-up'
                    }
                    else if (playerEvent.type == 'changes_down') {
                        type = 'substitution-down'
                    }
                    else if (playerEvent.type == 'yellow') {
                        if (hasYellowCard) {
                            type = 'yellow-red'
                            hasRedCard = true
                        }
                        else {
                            type = 'yellow'
                            hasYellowCard = true
                        }
                    }
                    else if (playerEvent.type == 'red') {
                        if (hasRedCard) {
                            continue;
                        }
                        else {
                            type = 'red'
                        }
                    }
                    else {
                        type = playerEvent.type
                    }
                    // console.log('player event', playerEvent.id, playerEvent.time, playerEvent.type)
                    playerEvents.push({
                        id: playerEvent.id,
                        time: playerEvent.time,
                        type: type
                    })
                }
                // filtered player with real supported events
                if (playerEvents.length > 0) {
                    teamPlayerEventList.push({
                        id: player.id,
                        number: player.number,
                        name: player.trueName,
                        isStarter: player.isStarting,
                        events: playerEvents,
                        goalCount: player.goalCount ? player.goalCount : 0
                    })
                } 
            }
            return teamPlayerEventList
        }
    },

    /**
     * @created
     * Set the data model via the match id, game id and passport.
     * Query the game info data according to the params and cookies.
     * All parameters are required. 
     * Use getPassport to ensure the authentication status.
     */
	created() {
        // validate the passport and prepair the one
        getPassport().then(res => {
            console.log('getPassport', res)
            if (res) {
                this.queryGameInfo()
                // this.queryGameEvent()
            }
        })
	}
}
</script>

<template>
    <!-- component -->
    <div class="match-team-game-confirmation">
        <!-- head start -->
        <div class="head">
            <game-summary
                :title="gameInfo.title"
                :time="gameInfo.time"
                :homeTeamName="gameInfo.homeTeamName"
                :homeTeamBadge="gameInfo.homeTeamBadge"
                :homeTeamScore="gameInfo.homeTeamScore"
                :awayTeamName="gameInfo.awayTeamName"
                :awayTeamBadge="gameInfo.awayTeamBadge"
                :awayTeamScore="gameInfo.awayTeamScore">
            </game-summary>
        </div>
        <!-- head end -->
        <!-- body start -->
        <div class="body">
            <van-tabs :active="selectedTeamTab" animated>
                <van-tab title="主队">
                    <team-player-events 
                        :teamName="homeTeamLabel"
                        :playerEvents="homePlayerEventList"
                        :players="homePlayerList"
                        :isEvent="isEvent"
                        @switch-tab="onHomeSwitchTab">
                    </team-player-events>
                </van-tab>
                <van-tab title="客队">
                    <team-player-events
                        :teamName="awayTeamLabel"
                        :playerEvents="awayPlayerEventList"
                        :players="awayPlayerList"
                        :isEvent="isEvent"
                        @switch-tab="onAwaySwitchTab">
                    </team-player-events>
                </van-tab>
            </van-tabs>
        </div>
        <!-- body end -->
        <!-- footer start -->
        <div class="footer">
            <div class="fixed-layer">
                <div class="layer-wrapper">
                    <div class="switch-bar">
                        <div class="switch-btn" @click="onSwitchBtnClick">
                            <span v-if="isEvent">查看球队名单</span>
                            <span v-else>仅查看事件</span>
                            <img class="right-arrow" src="@/assets/match-team-game/right-icon.svg"/>
                        </div>
                    </div>
                    <div class="indicator-bar">
                        <div class="indicator-wrapper">
                            <div class="indicator-unit goal-indicator">
                                <img src="@/assets/match-team-game/goal-symbol.svg"/>
                                <span class="label">进球</span>
                            </div>
                            <div class="indicator-unit penalty-goal-indicator">
                                <img src="@/assets/match-team-game/penalty-goal-symbol.svg"/>
                                <span class="label">点球</span>
                            </div>
                            <div class="indicator-unit own-goal-indicator">
                                <img src="@/assets/match-team-game/own-goal-symbol.svg"/>
                                <span class="label">乌龙球</span>
                            </div>
                            <div class="indicator-unit assist-indicator">
                                <img src="@/assets/match-team-game/assist-symbol.svg"/>
                                <span class="label">助攻</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="confirmedMessage" class="message-bar">
                        <span class="label">已确认</span>
                        <span class="content">{{ confirmedMessage }}</span>
                    </div>
                    <div class="operation-bar">
                        <div class="btn-group">
                            <div v-if="gameConfirmed" class="btn next-btn" @click="onBackBtnClick">
                                <span class="label">返回比赛详情</span>
                            </div>
                            <div v-else class="btn next-btn" @click="onComfirmBtnClick">
                                <span class="label">赛后信息确认</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- footer end -->
    </div>
</template>

<style scoped>
.match-team-game-confirmation {
    background: #F9F9F9;
    min-height: 100vh;
}
.footer {
    height: 156px;
}
.fixed-layer {
    position: fixed;
    width: 100%;
    bottom: 0;
    background: #FFFFFF;
}
.layer-wrapper {
    display: relative;
}
.switch-bar {
    height: 30px;
    text-align: center;
    background: #FFFFFF;
    line-height: 30px;
}
.switch-bar .switch-btn {
    display: inline-block;
    font-family: Source Han Sans;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.32px;
    color: #0FAC7F;
}
.switch-bar .right-arrow {
    position: absolute;
    top: 6px;
}
.indicator-bar {
    height: 30px;
    text-align: center;
    background: #FFFFFF;
    /**
     * avoid the compatiable issue on the app
     * NOT: align-content: center;
     * BUT: line-height: 1;
     */
    line-height: 30px;
}
.indicator-wrapper div {
    display: inline-block;
}
.indicator-unit img {
    margin-right: 4px;
}
.indicator-unit {
    margin-left: 10px;
    margin-right: 10px;
}
.indicator-unit .label {
    font-family: AlibabaPuHuiTi;
    font-size: 10px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #4D4D4D;
    bottom: 2px;
    position: relative;
}
.message-bar {
    margin-left: 12px;
    margin-right: 12px;
    width: 351px;
    height: 31px;
    border-radius: 2px;
    background: rgb(213,79,63,.1);
    line-height: 31px;
}
.message-bar .label {
    margin-left: 8px;
    font-family: AlibabaPuHuiTi;
    font-size: 11px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: normal;
    color: #1D2129;
}
.message-bar .content {
    margin-left: 7px;
    font-family: AlibabaPuHuiTi;
    font-size: 11px;
    font-weight: normal;
    line-height: 11px;
    letter-spacing: normal;
    color: #73767A;
}
.btn {
    height: 45px;
    text-align: center;
    display: inline-block;
    /**
     * avoid the compatiable issue on the app
     * NOT: align-content: center;
     * BUT: line-height: 1;
     */
    line-height: 45px;
}
.btn-group {
    margin: 10px 15px;
}
.next-btn {
    border-radius: 6px;
    background: #0FAC7F;
    width: 100%;
}
.next-btn .label {
    font-family: Source Han Sans;
    font-size: 15px;
    font-weight: 500;
    line-height: 15px;
    text-align: center;
    letter-spacing: normal;
    color: #FFFFFF;
}
.prev-btn {
    margin-right: 10px;
    width: 114px;
    border-radius: 6px;
    background: #FFFFFF;
    box-sizing: border-box;
    border: 1px solid #0FAC7F;
}
.prev-btn .label {
    font-family: Source Han Sans;
    font-size: 15px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: normal;
    color: #0FAC7F;
}
.confirm-btn {
    width: 219px;
    border-radius: 6px;
    background: #0FAC7F;
}
.confirm-btn .label {
    font-family: Source Han Sans;
    font-size: 15px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: normal;
    color: #FFFFFF;
}
</style>
<style>
/**
 * Dialog
 */
.van-dialog {
    border-radius: 4px;
}
.van-dialog__header {
    padding-top: 23px;
    font-family: Source Han Sans;
    font-size: 17px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: normal;
    color: #4D4D4D;
}
.van-dialog__message--has-title {
    padding-top: 20px;
    font-family: Source Han Sans;
    font-size: 13px;
    font-weight: normal;
    line-height: 15px;
    letter-spacing: normal;
    color: #4D4D4D;
}
.van-dialog__cancel {
    height: 36px;
    border-radius: 108px;
    background: #FFFFFF;
    box-sizing: border-box;
    border: 1px solid #0FAC7F;
}
.van-dialog__cancel .van-button__text {
    font-family: Source Han Sans;
    font-size: 13px;
    font-weight: normal;
    line-height: 35px;
    letter-spacing: 0.2px;
    color: #0FAC7F;
}
.van-dialog__confirm {
    margin-left: 15px;
    height: 36px;
    border-radius: 108px;
    background: #0FAC7F;
}
.van-dialog__confirm .van-button__text {
    font-family: Source Han Sans;
    font-size: 13px;
    font-weight: normal;
    line-height: 35px;
    letter-spacing: 0.2px;
    color: #FFFFFF;
}
.van-dialog__footer {
    margin: 11px 9px 19px 9px;
}
.game-confirm-dialog .van-dialog__footer {
    width: 114px;
    margin-left: auto;
    margin-right: auto;
}
.game-confirm-dialog .van-dialog__confirm {
    margin-left: 0;
}
</style>